import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Role } from '../../types';
import { Shield, Plus, Edit2, Trash2, X, Check } from 'lucide-react';

import { APP_MODULES, ModuleId } from '../../types';

const AVAILABLE_PERMISSIONS: { id: string; label: string; moduleId: ModuleId | 'system'; group?: string }[] = [
    { id: 'admin:view', label: 'Access Admin Dashboard', moduleId: 'admin' },
    { id: 'admin:full', label: 'Full Admin Access', moduleId: 'admin' },
    { id: 'settings:edit', label: 'Edit Global Settings', moduleId: 'admin' },
    { id: 'super:admin', label: 'Super Admin (Bypass all checks)', moduleId: 'admin' },

    // Admin Dashboard Tabs
    { id: 'admin:view:roles', label: 'View Roles Tab', moduleId: 'admin', group: 'Admin Dashboard Tabs' },
    { id: 'admin:view:users', label: 'View Users Tab', moduleId: 'admin', group: 'Admin Dashboard Tabs' },
    { id: 'admin:view:departments', label: 'View Departments Tab', moduleId: 'admin', group: 'Admin Dashboard Tabs' },
    { id: 'admin:view:settings', label: 'View Settings Tab', moduleId: 'admin', group: 'Admin Dashboard Tabs' },
    { id: 'admin:view:security', label: 'View E2EE Security Tab', moduleId: 'admin', group: 'Admin Dashboard Tabs' },
    { id: 'admin:view:audit', label: 'View Audit Logs Tab', moduleId: 'admin', group: 'Admin Dashboard Tabs' },

    // Settings Sub-Tabs
    { id: 'settings:general', label: 'Settings: General', moduleId: 'admin', group: 'Settings Sub-Tabs' },
    { id: 'settings:email', label: 'Settings: Email', moduleId: 'admin', group: 'Settings Sub-Tabs' },
    { id: 'settings:storage', label: 'Settings: Storage', moduleId: 'admin', group: 'Settings Sub-Tabs' },
    { id: 'settings:security', label: 'Settings: Security', moduleId: 'admin', group: 'Settings Sub-Tabs' },
    { id: 'settings:data', label: 'Settings: Data Policy', moduleId: 'admin', group: 'Settings Sub-Tabs' },
    { id: 'settings:desktop', label: 'Settings: Desktop App', moduleId: 'admin', group: 'Settings Sub-Tabs' },

    // Chat
    { id: 'create:channel', label: 'Create Groups', moduleId: 'chat' },
    { id: 'create:topic', label: 'Create Topics', moduleId: 'chat' },

    // Tickets
    { id: 'view:ticket_dashboard', label: 'View Ticket Dashboard', moduleId: 'tickets' },
    { id: 'tickets:view_deleted', label: 'View Deleted Tickets', moduleId: 'tickets' },
];


const cardStyle: React.CSSProperties = {
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-color)',
    overflow: 'hidden'
};

const headerStyle: React.CSSProperties = {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
};

export function RoleManagement() {
    const { roles, settings, updateSettings, createRole, updateRole, deleteRole } = useApp();
    const [editingRole, setEditingRole] = useState<Partial<Role> | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savingDefault, setSavingDefault] = useState(false);

    const handleEdit = (role: Role) => {
        setEditingRole({ ...role });
        setIsCreating(false);
        setError(null);
    };

    const handleCreate = () => {
        setEditingRole({ name: '', description: '', permissions: [] });
        setIsCreating(true);
        setError(null);
    };

    const handleSave = async () => {
        if (!editingRole || !editingRole.name) {
            setError('Role name is required');
            return;
        }
        try {
            if (isCreating) {
                await createRole(editingRole);
            } else if (editingRole.id) {
                await updateRole(editingRole.id, editingRole);
            }
            setEditingRole(null);
            setIsCreating(false);
        } catch (err) {
            setError('Failed to save role');
        }
    };

    const handleDelete = async (id: string) => {
        if (confirm('Are you sure you want to delete this role?')) {
            await deleteRole(id);
        }
    };

    const togglePermission = (permId: string) => {
        if (!editingRole) return;
        const currentPerms = editingRole.permissions || [];
        if (currentPerms.includes(permId)) {
            setEditingRole({ ...editingRole, permissions: currentPerms.filter(p => p !== permId) });
        } else {
            setEditingRole({ ...editingRole, permissions: [...currentPerms, permId] });
        }
    };

    // Edit/Create Form
    if (editingRole) {
        return (
            <div style={cardStyle}>
                <div style={headerStyle}>
                    <h2 style={{ fontSize: 16, fontWeight: 600 }}>{isCreating ? 'Create New Role' : 'Edit Role'}</h2>
                    <button onClick={() => setEditingRole(null)} className="btn btn-ghost btn-icon">
                        <X size={18} />
                    </button>
                </div>
                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {error && <div style={{ background: '#fee', color: '#c00', padding: 12, borderRadius: 6, fontSize: 13 }}>{error}</div>}
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Role Name</label>
                        <input
                            type="text"
                            className="input"
                            value={editingRole.name}
                            onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                            placeholder="e.g. Moderator"
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Description</label>
                        <input
                            type="text"
                            className="input"
                            value={editingRole.description || ''}
                            onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                            placeholder="Role purpose..."
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 13 }}>Module Access & Permissions</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {APP_MODULES.map(module => {
                                const isModuleAllowed = editingRole.allowed_modules?.includes(module.id);
                                const modulePermissions = AVAILABLE_PERMISSIONS.filter(p => p.moduleId === module.id);
                                const isSystem = module.isSystem;

                                return (
                                    <div key={module.id} style={{
                                        border: '1px solid var(--border-color)',
                                        borderRadius: 8,
                                        overflow: 'hidden'
                                    }}>
                                        {/* Module Header */}
                                        <div style={{
                                            padding: '12px 16px',
                                            background: 'var(--bg-tertiary)',
                                            borderBottom: modulePermissions.length > 0 ? '1px solid var(--border-color)' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isModuleAllowed}
                                                    disabled={isSystem}
                                                    onChange={() => {
                                                        const currentModules = editingRole.allowed_modules || [];
                                                        let newModules;
                                                        if (currentModules.includes(module.id)) {
                                                            newModules = currentModules.filter(m => m !== module.id);
                                                        } else {
                                                            newModules = [...currentModules, module.id];
                                                        }
                                                        setEditingRole({ ...editingRole, allowed_modules: newModules });
                                                    }}
                                                    style={{ width: 16, height: 16, cursor: isSystem ? 'not-allowed' : 'pointer' }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {module.name}
                                                        {isSystem && <span style={{ fontSize: 10, background: '#e0e7ff', color: '#4338ca', padding: '2px 6px', borderRadius: 4 }}>Required</span>}
                                                    </div>
                                                    <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{module.description}</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Module Permissions */}
                                        {modulePermissions.length > 0 && (() => {
                                            // Group permissions by their optional `group` label
                                            const groups: Record<string, typeof modulePermissions> = {};
                                            modulePermissions.forEach(perm => {
                                                const key = perm.group || '';
                                                if (!groups[key]) groups[key] = [];
                                                groups[key].push(perm);
                                            });

                                            const renderPerm = (perm: typeof modulePermissions[0]) => (
                                                <div key={perm.id} style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '8px 0',
                                                    cursor: isModuleAllowed ? 'pointer' : 'not-allowed'
                                                }} onClick={() => isModuleAllowed && togglePermission(perm.id)}>
                                                    <div style={{ width: 28 }} /> {/* Indent */}
                                                    <input
                                                        type="checkbox"
                                                        checked={editingRole.permissions?.includes(perm.id)}
                                                        onChange={() => isModuleAllowed && togglePermission(perm.id)}
                                                        disabled={!isModuleAllowed}
                                                        style={{ marginRight: 12, cursor: isModuleAllowed ? 'pointer' : 'not-allowed' }}
                                                    />
                                                    <div>
                                                        <div style={{ fontSize: 13 }}>{perm.label}</div>
                                                        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>ID: {perm.id}</div>
                                                    </div>
                                                </div>
                                            );

                                            return (
                                                <div style={{ padding: '8px 16px', background: isModuleAllowed ? 'transparent' : 'var(--bg-secondary)', opacity: isModuleAllowed ? 1 : 0.6 }}>
                                                    {/* Ungrouped perms first */}
                                                    {(groups[''] || []).map(renderPerm)}
                                                    {/* Named groups */}
                                                    {Object.entries(groups).filter(([key]) => key !== '').map(([groupName, perms]) => (
                                                        <div key={groupName}>
                                                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 0 4px 28px', borderTop: '1px solid var(--border-color)', marginTop: 4 }}>
                                                                {groupName}
                                                            </div>
                                                            {perms.map(renderPerm)}
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()}

                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 8 }}>
                        <button onClick={() => setEditingRole(null)} className="btn btn-ghost">Cancel</button>
                        <button onClick={handleSave} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Check size={16} />
                            Save Role
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Role List
    return (
        <div style={cardStyle}>
            <div style={headerStyle}>
                <div>
                    <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Roles & Permissions</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Manage user access levels.</p>
                </div>
                <button onClick={handleCreate} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Plus size={16} />
                    New Role
                </button>
            </div>

            {/* Default Role Selector */}
            <div style={{
                padding: '16px 20px',
                background: 'var(--bg-tertiary)',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                gap: 12
            }}>
                <label style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Default Role for New Users:
                </label>
                <select
                    className="input select"
                    style={{ maxWidth: 200, padding: '6px 10px', fontSize: 13 }}
                    value={settings?.default_role_id || ''}
                    disabled={savingDefault}
                    onChange={async (e) => {
                        setSavingDefault(true);
                        try {
                            await updateSettings({ default_role_id: e.target.value || undefined });
                        } finally {
                            setSavingDefault(false);
                        }
                    }}
                >
                    <option value="">No default (require selection)</option>
                    {roles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                </select>
                {savingDefault && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Saving...</span>}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ background: 'var(--bg-tertiary)', fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600 }}>Role</th>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600 }}>Permissions</th>
                        <th style={{ padding: '12px 20px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {roles.map((role, i) => (
                        <tr key={role.id} style={{ borderBottom: i < roles.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                            <td style={{ padding: '14px 20px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Shield size={16} color="var(--text-muted)" />
                                    <span style={{ fontWeight: 500 }}>{role.name}</span>
                                </div>
                            </td>
                            <td style={{ padding: '14px 20px', color: 'var(--text-muted)', fontSize: 13 }}>{role.description}</td>
                            <td style={{ padding: '14px 20px' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {role.permissions.slice(0, 2).map(p => {
                                        const label = AVAILABLE_PERMISSIONS.find(ap => ap.id === p)?.label || p;
                                        return (
                                            <span key={p} style={{
                                                background: 'var(--accent-primary-light, #e0f2fe)',
                                                color: 'var(--accent-primary)',
                                                padding: '2px 8px',
                                                borderRadius: 4,
                                                fontSize: 11,
                                                fontWeight: 500
                                            }}>{label}</span>
                                        );
                                    })}
                                    {role.permissions.length > 2 && (
                                        <span style={{ background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                                            +{role.permissions.length - 2}
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                                <button onClick={() => handleEdit(role)} className="btn btn-ghost btn-icon btn-sm" style={{ marginRight: 4 }}>
                                    <Edit2 size={16} />
                                </button>
                                <button
                                    onClick={() => handleDelete(role.id)}
                                    className="btn btn-ghost btn-icon btn-sm"
                                    disabled={role.id.includes('super-admin')}
                                    style={{ opacity: role.id.includes('super-admin') ? 0.3 : 1 }}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
